/**
 * ContentHeader — Horizontal menu bar at top of content area
 *
 * Shows the current section title + horizontal tabs for sub-navigation.
 * Changes based on which rail item is active.
 */

import { Link, useLocation } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

export interface ContentTab {
  label: string;
  path: string;
  icon?: LucideIcon;
}

interface ContentHeaderProps {
  title: string;
  tabs: ContentTab[];
  /** Optional: right-side actions */
  actions?: React.ReactNode;
}

export function ContentHeader({ title, tabs, actions }: ContentHeaderProps) {
  const location = useLocation();

  return (
    <div className="border-b border-border/40 bg-background shrink-0">
      <div className="flex items-center justify-between px-6">
        <div className="flex items-center gap-6">
          <h1 className="text-lg font-semibold py-3 shrink-0">{title}</h1>
          <div className="flex items-center gap-0.5">
            {tabs.map(tab => {
              // Longest-prefix-wins: don't activate this tab if a more specific tab also matches
              const exactMatch = location.pathname === tab.path || location.pathname === tab.path + '/';
              const prefixMatch = location.pathname.startsWith(tab.path + '/');
              const moreSpecificTabMatches = prefixMatch && tabs.some(other =>
                other.path !== tab.path &&
                other.path.startsWith(tab.path + '/') &&
                (location.pathname === other.path || location.pathname.startsWith(other.path + '/'))
              );
              const isActive = exactMatch || (prefixMatch && !moreSpecificTabMatches);
              const Icon = tab.icon;

              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors",
                    isActive
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                  )}
                >
                  {Icon && <Icon className="h-4 w-4" />}
                  {tab.label}
                </Link>
              );
            })}
          </div>
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}
